/**
 * PageHeader — canonical page-level header component.
 *
 * Standard: text-xl / font-semibold / icon h-4 w-4 inside a w-8 h-8 rounded box.
 * All pages should use this component rather than rolling their own <h1>.
 *
 *   [icon box]  [title / subtitle]     [right-side actions]
 *
 * On a narrow screen (a phone browser) the actions wrap onto their own line
 * under the title instead of running off the right edge and squeezing the
 * title into a sliver. Wide screens keep the single row.
 */

import type { ReactNode } from 'react';

interface PageHeaderProps {
  /** Icon element — already sized and colored (e.g. `<Layers className="h-4 w-4 text-[var(--text-ter)]" />`). */
  icon: ReactNode;
  /**
   * Background class for the icon wrapper box.
   * @default 'bg-[var(--hover-tint)]'
   */
  iconBg?: string;
  /** Page title. */
  title: string;
  /** Optional subtitle / meta line below the title. */
  subtitle?: ReactNode;
  /** Optional right-side actions area. */
  actions?: ReactNode;
  /** Extra classes on the outer flex container. */
  className?: string;
  /** Bottom margin class — defaults to 'mb-6'. Pass '' to omit. */
  mb?: string;
}

export function PageHeader({
  icon,
  iconBg = 'bg-[var(--hover-tint)] dark:bg-[var(--surface)]',
  title,
  subtitle,
  actions,
  className = '',
  mb = 'mb-6',
}: PageHeaderProps) {
  return (
    <div className={`flex flex-wrap items-center justify-between gap-3 ${mb} ${className}`.trim()}>
      {/* Starts at 16rem and grows: a long subtitle wraps inside the title
          block on a wide screen; the row wraps only when 16rem and the
          actions no longer fit side by side. */}
      <div className="flex min-w-0 flex-[1_1_16rem] items-center gap-3">
        <div className={`w-8 h-8  flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          {icon}
        </div>
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] font-heading leading-tight">
            {title}
          </h1>
          {subtitle && (
            <div className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] font-light mt-0.5 leading-tight">
              {subtitle}
            </div>
          )}
        </div>
      </div>

      {/* max-w-full: once the actions have a line of their own, they wrap
          inside the screen instead of running past its edge — and so does a
          row of buttons a page passes in as one <div>. */}
      {actions && (
        <div className="flex max-w-full flex-wrap items-center gap-2 [&>div]:max-w-full [&>div]:flex-wrap">
          {actions}
        </div>
      )}
    </div>
  );
}

export default PageHeader;
